import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ThemeToggle from "@/components/ThemeToggle";
import { toast } from "sonner";
import {
  Settings as SettingsIcon, ShieldCheck, Bell, Users, Palette,
  LogOut, Trash2, ChevronRight, Loader2, KeyRound,
} from "lucide-react";

/** Student Settings page — one-stop for social, privacy, appearance and
 * account controls. Reachable from the mobile "More" sheet and the web
 * sidebar. Delete account + password change deep-link into the Profile
 * page which already owns those flows (single source of truth).
 */
export default function StudentSettings() {
  const nav = useNavigate();
  const { logout } = useAuth();
  const [social, setSocial] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/me/social")
      .then((r) => setSocial(r.data))
      .catch(() => setSocial({ social_privacy: {} }));
  }, []);

  const updatePrivacy = async (patch) => {
    if (!social) return;
    // Optimistic UI
    const nextPrivacy = { ...(social.social_privacy || {}), ...patch };
    setSocial((s) => ({ ...s, social_privacy: nextPrivacy }));
    setSaving(true);
    try {
      const r = await api.put("/me/social/privacy", patch);
      setSocial((s) => ({ ...s, social_privacy: r.data.social_privacy }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save preference");
      // Revert
      setSocial((s) => ({ ...s, social_privacy: social.social_privacy || {} }));
    } finally {
      setSaving(false);
    }
  };

  const privacy = social?.social_privacy || {};

  return (
    <div className="max-w-2xl space-y-6" data-testid="student-settings-page">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 flex items-center gap-2">
          <SettingsIcon className="w-3.5 h-3.5" /> Settings
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Preferences</h1>
        <p className="text-slate-600 dark:text-slate-300 mt-1 text-sm">
          Manage your privacy, notifications and account.
        </p>
      </div>

      {/* Social & Privacy */}
      <Card className="p-5 border-slate-200 dark:border-slate-700 space-y-5" data-testid="settings-section-social">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-indigo-600" />
          <h2 className="font-display text-lg font-bold">Social & Privacy</h2>
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400 ml-1" />}
        </div>

        {!social?.social_setup_complete ? (
          <div className="rounded-md border border-dashed border-slate-300 dark:border-slate-600 p-4 text-sm">
            <p className="text-slate-600 dark:text-slate-300">
              Set up your social handle to enable friends, feed and privacy controls.
            </p>
            <Button
              className="mt-3"
              onClick={() => nav("/student/friends/setup")}
              data-testid="settings-setup-social-btn"
            >
              Set up social profile
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <SettingRow
              label="Findable by QR code"
              description="Who can add you as a friend by scanning your QR."
              data-testid="setting-findable-by-qr"
            >
              <Select
                value={privacy.findable_by_qr || "everyone"}
                onValueChange={(v) => updatePrivacy({ findable_by_qr: v })}
              >
                <SelectTrigger className="w-[180px]" data-testid="findable-by-qr-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="everyone">Everyone</SelectItem>
                  <SelectItem value="friends_of_friends">Friends of friends</SelectItem>
                  <SelectItem value="nobody">Nobody</SelectItem>
                </SelectContent>
              </Select>
            </SettingRow>

            <SettingRow
              label="Share achievements with friends"
              description="Post your badges, streaks and mock exam results to your friends' feed."
              data-testid="setting-share-achievements"
            >
              <Select
                value={privacy.share_achievements || "on"}
                onValueChange={(v) => updatePrivacy({ share_achievements: v })}
              >
                <SelectTrigger className="w-[140px]" data-testid="share-achievements-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="on">On</SelectItem>
                  <SelectItem value="off">Off</SelectItem>
                  <SelectItem value="ask">Ask each time</SelectItem>
                </SelectContent>
              </Select>
            </SettingRow>

            <SettingRow
              label="Show me on the leaderboard"
              description="Appear on the course-wide accuracy and streak boards."
              data-testid="setting-show-on-leaderboard"
            >
              <Switch
                checked={(privacy.show_on_leaderboard ?? "on") === "on"}
                onCheckedChange={(c) => updatePrivacy({ show_on_leaderboard: c ? "on" : "off" })}
                data-testid="show-on-leaderboard-switch"
              />
            </SettingRow>

            <SettingRow
              label="Notify me on friend reactions"
              description="Get a push notification when a friend reacts to your post."
              data-testid="setting-notify-on-reactions"
            >
              <Switch
                checked={!!privacy.notify_on_reactions}
                onCheckedChange={(c) => updatePrivacy({ notify_on_reactions: c })}
                data-testid="notify-on-reactions-switch"
              />
            </SettingRow>
          </div>
        )}
      </Card>

      {/* Appearance */}
      <Card className="p-5 border-slate-200 dark:border-slate-700" data-testid="settings-section-appearance">
        <div className="flex items-center gap-2 mb-4">
          <Palette className="w-4 h-4 text-fuchsia-600" />
          <h2 className="font-display text-lg font-bold">Appearance</h2>
        </div>
        <SettingRow
          label="Theme"
          description="Switch between light and dark modes."
        >
          <ThemeToggle />
        </SettingRow>
      </Card>

      {/* Account */}
      <Card className="p-5 border-slate-200 dark:border-slate-700 space-y-3" data-testid="settings-section-account">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="w-4 h-4 text-emerald-600" />
          <h2 className="font-display text-lg font-bold">Account</h2>
        </div>

        <NavRow
          icon={KeyRound}
          label="Profile & password"
          description="Edit your name, email, and change your password."
          onClick={() => nav("/student/profile")}
          testid="settings-open-profile"
        />

        <NavRow
          icon={Bell}
          label="Notifications"
          description="Manage push and email notifications."
          onClick={() => nav("/student/profile#notifications")}
          testid="settings-open-notifications"
        />

        <NavRow
          icon={LogOut}
          label="Log out"
          description="Sign out on this device."
          onClick={() => { logout(); nav("/login"); }}
          testid="settings-logout"
          tone="warn"
        />

        <NavRow
          icon={Trash2}
          label="Delete account"
          description="Permanently remove your PrepMasters account and data."
          onClick={() => nav("/student/profile#delete-account")}
          testid="settings-open-delete-account"
          tone="danger"
        />
      </Card>
    </div>
  );
}

function SettingRow({ label, description, children, ...rest }) {
  return (
    <div className="flex items-start justify-between gap-4" {...rest}>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">{label}</div>
        {description && (
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{description}</div>
        )}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

function NavRow({ icon: Icon, label, description, onClick, testid, tone = "default" }) {
  const toneClass =
    tone === "danger"
      ? "text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/40"
      : tone === "warn"
      ? "text-amber-800 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/40"
      : "text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800/60";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-3 rounded-lg px-3 py-3 transition ${toneClass}`}
      data-testid={testid}
    >
      <Icon className="w-4 h-4 shrink-0" />
      <div className="min-w-0 flex-1 text-left">
        <div className="text-sm font-semibold">{label}</div>
        {description && (
          <div className="text-xs opacity-70 mt-0.5">{description}</div>
        )}
      </div>
      <ChevronRight className="w-4 h-4 opacity-40 shrink-0" />
    </button>
  );
}
